import { FC } from 'react';
import { Stack, Paper, Text, Button, Loader } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { DiagnosticReport, Patient } from '@medplum/fhirtypes';
import { ReportCard } from '@/pages/ainigma/patient/files-tab/components/ReportCard';

export interface ChecksListProps {
  type: 'preImplantationChecks' | 'prenatalChecks';
  checks: DiagnosticReport[];
  patient: Patient;
  onAdd: () => void;
  onEdit: (check: DiagnosticReport) => void;
  onDelete: (checkId: string) => void;
  deletingId?: string | null;
  readonly?: boolean;
}

export const ChecksList: FC<ChecksListProps> = ({
  type,
  checks,
  patient,
  onAdd,
  onEdit,
  onDelete,
  deletingId = null,
  readonly = false,
}) => {
  const { t } = useTranslation(['patient-pages']);

  const handleDelete = (report: DiagnosticReport) => {
    if (report.id) {
      onDelete(report.id);
    }
  };

  return (
    <Stack gap="xs">
      <Text fw={600} size="sm">
        {t(`medicalHistory.${type}.title`)}
      </Text>
      {/* Empty state */}
      {checks.length === 0 ? (
        <Paper withBorder radius="md" p="xl" bg="gray.0">
          <Text size="sm" c="dimmed" ta="center">
            {t(`medicalHistory.${type}.noData`, {
              defaultValue: `No ${type} recorded`,
            })}
          </Text>
        </Paper>
      ) : (
        <Stack gap="xs">
          {checks.map((check) => {
            const isDeleting = deletingId === check.id;

            return isDeleting ? (
              <Paper
                key={check.id}
                withBorder
                radius="md"
                p="sm"
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: '100px',
                }}
              >
                <Loader size="sm" />
              </Paper>
            ) : (
              <ReportCard
                key={check.id}
                report={check}
                patient={patient}
                onEdit={onEdit}
                onDelete={handleDelete}
                readonly={readonly}
              />
            );
          })}
        </Stack>
      )}
      {!readonly && (
        <Button
          leftSection={<IconPlus size={16} />}
          variant="light"
          size="sm"
          onClick={onAdd}
        >
          {t(`medicalHistory.${type}.add`, {
            defaultValue: `Add ${type}`,
          })}
        </Button>
      )}
    </Stack>
  );
};

export default ChecksList;
